import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../auth/AuthContext';
import { apiErrorMessage, apiErrorCode } from '../../api/client';
import { AuthInput } from '../../components/auth/AuthInput';
import { PasswordInput } from '../../components/auth/PasswordInput';
import { GoogleButton, AuthDivider } from '../../components/auth/GoogleButton';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: typeof errors = {};
    if (!EMAIL_RE.test(email)) next.email = 'Enter a valid email address';
    if (password.length === 0) next.password = 'Enter your password';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      if (apiErrorCode(err) === 'EMAIL_NOT_VERIFIED') {
        toast('Verify your email to continue — request a code below', { icon: '✉️' });
        navigate('/verify-email', { state: { email } });
        return;
      }
      toast.error(apiErrorMessage(err, 'Sign-in failed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section data-testid="page-login" className="mx-auto max-w-sm pt-6">
      <h1 className="text-2xl font-bold">Sign in</h1>
      <div className="mt-6">
        <GoogleButton label="Continue with Google" />
        <AuthDivider />
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <AuthInput
            label="Email"
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
          />
          <PasswordInput
            label="Password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
          />
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-emerald-700 px-4 py-2.5 font-medium text-white hover:bg-emerald-800 disabled:opacity-50"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <div className="mt-4 flex justify-between text-sm">
          <Link to="/reset-password" className="text-emerald-700 hover:underline">
            Forgot password?
          </Link>
          <Link to="/register" className="text-emerald-700 hover:underline">
            Create account
          </Link>
        </div>
      </div>
    </section>
  );
}
